import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Navigation.css';
import logo from '../../img/apc-logo-removebg.png';

const Navigation = () => {
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const desktopDropdownRef = useRef(null);
    const mobileDropdownRef = useRef(null);
    const mobileMenuRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            setIsScrolled(scrollPosition > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isDesktopDropdown = desktopDropdownRef.current && desktopDropdownRef.current.contains(event.target);
            const isMobileDropdown = mobileDropdownRef.current && mobileDropdownRef.current.contains(event.target);
            const isDropdownToggle = event.target.closest('.dropdown-toggle');
            
            if (!isDesktopDropdown && !isMobileDropdown && !isDropdownToggle) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    useEffect(() => {
        const handleClickOutsideMobile = (event) => {
            if (
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(event.target) &&
                !event.target.closest('.hamburger')
            ) {
                setIsMobileMenuOpen(false);
            }
        };
        
        if (isMobileMenuOpen) {
            document.addEventListener('mousedown', handleClickOutsideMobile);
            document.addEventListener('touchstart', handleClickOutsideMobile);
        } else {
            document.removeEventListener('mousedown', handleClickOutsideMobile);
            document.removeEventListener('touchstart', handleClickOutsideMobile);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideMobile);
            document.removeEventListener('touchstart', handleClickOutsideMobile);
        };
    }, [isMobileMenuOpen]);

    // Reset dropdown state when mobile menu closes
    useEffect(() => {
        if (!isMobileMenuOpen) {
            setIsDropdownOpen(false);
        }
    }, [isMobileMenuOpen]);

    const toggleDropdown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDropdownOpen(!isDropdownOpen);
    };

    const toggleMobileMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Allow mobile menu on mobile devices OR when navbar is scrolled (desktop uses mobile menu when scrolled)
        const isMobile = window.innerWidth <= 991;
        const isDesktopScrolled = window.innerWidth >= 992 && isScrolled;
        
        if (isMobile || isDesktopScrolled) {
            setIsMobileMenuOpen(!isMobileMenuOpen);
            // Close dropdown when opening mobile menu
            setIsDropdownOpen(false);
        } else {
            // Close mobile menu on desktop when not scrolled
            setIsMobileMenuOpen(false);
            setIsDropdownOpen(false);
        }
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
        setIsDropdownOpen(false);
    };

    const closeDropdown = () => setIsDropdownOpen(false);

    const handleContactClick = () => {
        navigate('/Contact');
        // Ensure page scrolls to top when contact link is clicked
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
    };

    const handleHomeClick = () => {
        navigate('/');
        // Ensure page scrolls to top when home link is clicked
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
    };

    const handleOfficePolicyClick = () => {
        navigate('/OfficeInfo/OfficePolicy');
        // Ensure page scrolls to top when office policy link is clicked
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
    };

    const handleAboutClick = () => {
        navigate('/About');
        // Ensure page scrolls to top when Our Team link is clicked
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
    };

    const handleOfficeHoursClick = () => {
        navigate('/Contact');
        // Navigate to contact page and scroll to hanover section
        setTimeout(() => {
            const element = document.getElementById('hanover');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            } else {
                // If element not found, try again after a longer delay
                setTimeout(() => {
                    const element = document.getElementById('hanover');
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 200);
            }
        }, 100);
    };

    const handleInsurancesClick = () => {
        navigate('/OfficeInfo/Insurances');
        // Ensure page scrolls to top when insurances link is clicked
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
    };

    const handleTelemedicineClick = () => {
        navigate('/OfficeInfo/Telemedicine');
        // Ensure page scrolls to top when telemedicine link is clicked
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
    };

    const handleNewPatientsClick = () => {
        navigate('/OfficeInfo/NewPatients');
        // Ensure page scrolls to top when new patients link is clicked
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
    };

    // Enhanced navigation handlers that prevent default behavior and ensure proper routing
    const handleNavigation = (path, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        navigate(path);
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
    };

    // Special handler for Office Hours that scrolls to the Our Locations section
    const handleOfficeHoursNavigation = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        navigate('/Contact');
        setTimeout(() => {
            const element = document.getElementById('contact-directions');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            } else {
                // If element not found, try again after a longer delay
                setTimeout(() => {
                    const element = document.getElementById('contact-directions');
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 200);
            }
        }, 100);
    };

    return (
        <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
            <div className={`nav-container ${isScrolled ? 'scrolled' : ''}`}>
                <Link to="/" className={`nav-logo ${isScrolled ? 'scrolled' : ''}`} onClick={(e) => handleNavigation('/', e)}>
                    <img src={logo} alt="Atlantic PC Logo" />
                </Link>
                
                {/* Desktop Navigation - Only show when not scrolled */}
                {!isScrolled && (
                    <div id="nav-box">
                        <ul>
                            <li><Link to="/" onClick={(e) => handleNavigation('/', e)}>Home</Link></li>
                            <li><Link to="/About" onClick={(e) => handleNavigation('/About', e)}>Our Team</Link></li>
                            <li className="dropdown" ref={desktopDropdownRef}>
                                <button 
                                    className="dropdown-toggle"
                                    onClick={toggleDropdown}
                                    aria-expanded={isDropdownOpen}
                                >
                                    Office Info
                                </button>
                                <div className={`dropdown-menu ${isDropdownOpen ? 'show' : ''}`}>
                                    <button className="dropdown-item" onClick={() => { closeDropdown(); handleNavigation('/OfficeInfo/NewPatients'); }}>New Patients</button>
                                    <button className="dropdown-item" onClick={() => { closeDropdown(); handleOfficeHoursNavigation(); }}>Office Hours</button>
                                    <button className="dropdown-item" onClick={() => { closeDropdown(); handleNavigation('/OfficeInfo/Insurances'); }}>Insurances</button>
                                    <button className="dropdown-item" onClick={() => { closeDropdown(); handleNavigation('/OfficeInfo/OfficePolicy'); }}>Office Policy</button>
                                    <button className="dropdown-item" onClick={() => { closeDropdown(); handleNavigation('/OfficeInfo/Telemedicine'); }}>Telemedicine</button>
                                </div>
                            </li>
                            <li><Link to="/Contact" onClick={(e) => handleNavigation('/Contact', e)}>Contact</Link></li>
                        </ul>
                    </div>
                )}
                
                {/* Beautiful Green-Themed Buttons - Always show on desktop */}
                <div className="nav-buttons">
                    <a 
                        href="tel:+17818715030" 
                        className="appointment-button"
                    >
                        <span>Schedule Appointment</span>
                    </a>
                    <a 
                        href="https://www.questdiagnostics.com/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="patient-portal-button"
                    >
                        <span>Patient Portal</span>
                    </a>
                </div>
                
                <button 
                    className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`} 
                    onClick={toggleMobileMenu}
                    aria-label="Toggle navigation menu"
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>

                <div 
                    className={`navbar-collapse ${isMobileMenuOpen ? 'show' : ''}`} 
                    ref={mobileMenuRef}
                >
                    <ul className="navbar-nav">
                        <li className="nav-item">
                            <Link to="/" className="nav-link" onClick={(e) => { setIsMobileMenuOpen(false); handleNavigation('/', e); }}>Home</Link>
                        </li>
                        <li className="nav-item">
                            <Link to="/About" className="nav-link" onClick={(e) => { setIsMobileMenuOpen(false); handleNavigation('/About', e); }}>Our Team</Link>
                        </li>
                        <li className="nav-item dropdown" ref={mobileDropdownRef}>
                            <button 
                                className="nav-link dropdown-toggle"
                                onClick={toggleDropdown}
                                aria-expanded={isDropdownOpen}
                            >
                                Office Info
                            </button>
                            <div className={`dropdown-menu ${isDropdownOpen ? 'show' : ''}`}>
                                <button className="dropdown-item" onClick={() => { closeDropdown(); setIsMobileMenuOpen(false); handleNavigation('/OfficeInfo/NewPatients'); }}>New Patients</button>
                                <button className="dropdown-item" onClick={() => { closeDropdown(); setIsMobileMenuOpen(false); handleOfficeHoursNavigation(); }}>Office Hours</button>
                                <button className="dropdown-item" onClick={() => { closeDropdown(); setIsMobileMenuOpen(false); handleNavigation('/OfficeInfo/Insurances'); }}>Insurances</button>
                                <button className="dropdown-item" onClick={() => { closeDropdown(); setIsMobileMenuOpen(false); handleNavigation('/OfficeInfo/OfficePolicy'); }}>Office Policy</button>
                                <button className="dropdown-item" onClick={() => { closeDropdown(); setIsMobileMenuOpen(false); handleNavigation('/OfficeInfo/Telemedicine'); }}>Telemedicine</button>
                            </div>
                        </li>
                        <li className="nav-item">
                            <Link to="/Contact" className="nav-link" onClick={(e) => { setIsMobileMenuOpen(false); handleNavigation('/Contact', e); }}>Contact</Link>
                        </li>
                        <li className="nav-item">
                            <a 
                                href="tel:+17818715030" 
                                className="nav-link appointment-button-mobile"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <span>Schedule Appointment</span>
                            </a>
                        </li>
                        <li className="nav-item">
                            <a 
                                href="https://www.questdiagnostics.com/" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="nav-link patient-portal-mobile"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <span>Patient Portal</span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </nav>
    );
};

export default Navigation; 